// Contact page — GDPR-compliant form.
// Sends to /api/contact and notifies SUPPORT_EMAIL via Resend backend.
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, Mail } from "lucide-react";
import Layout from "@/components/Layout";
import { useT, useLang } from "@/lib/i18n";
import { PRODUCTS } from "@/lib/products";
import { apiFetch } from "@/lib/api";

// Declarative beta presets — derived from PRODUCTS where status === "coming_soon".
// When a product flips to "available" in products.js, its preset auto-disappears,
// the badge disappears (ProductFamilyPage reads the same `status`), and the
// CRM beta-hook stops firing for it. Single source of truth.
const BETA_PRESETS = Object.values(PRODUCTS)
  .filter((p) => p.status === "coming_soon")
  .reduce((acc, p) => {
    const capitalised = p.slug.charAt(0).toUpperCase() + p.slug.slice(1);
    acc[`${p.slug}-beta`] = { slug: p.slug, label: `${capitalised} — Devenir Bêta testeur` };
    return acc;
  }, {});

const ContactPage = () => {
  const t = useT();
  const lang = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [betaProduct, setBetaProduct] = useState(null);
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot — must stay empty
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const preset = params.get("subject");
    if (preset && BETA_PRESETS[preset]) {
      const { slug, label } = BETA_PRESETS[preset];
      // eslint-disable-next-line
      setForm((f) => ({ ...f, subject: label })); setBetaProduct(slug);
    }
  }, [params]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setErrorMsg(t("contact.error.consent_required"));
      setStatus("err");
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    const { ok, error: err } = await apiFetch(`/api/contact`, {
      method: "POST",
      body: JSON.stringify({
        ...form,
        lang: lang || "fr",
        beta_product: betaProduct || undefined,
        consent: true,
        consent_text: t("contact.consent.label"),
        website, // honeypot — should stay empty
      }),
    });
    if (ok) {
      setStatus("ok");
      setForm({ name: "", email: "", subject: "", message: "" });
      setConsent(false);
      // Redirect to the public /newsletter landing after a brief moment so
      // the visitor sees the inline "message sent" state, then lands on
      // the newsletter page (with a confirmation banner) inviting them to
      // stay in touch. Avoids the blank/scroll-jumped feel of staying put.
      setTimeout(() => {
        navigate("/newsletter?contact_sent=1");
      }, 900);
    } else {
      setErrorMsg(err || t("contact.error.generic"));
      setStatus("err");
    }
  };

  return (
    <Layout>
      <section className="py-16 md:py-24 bg-[color:var(--gv-mist)]">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="eyebrow mb-3">{t("contact.eyebrow")}</div>
            <h1 className="font-display text-4xl md:text-5xl text-[color:var(--gv-ink)] mb-3" data-testid="contact-title">
              {t("contact.title")}
            </h1>
            <p className="text-[color:var(--gv-ink)]/70 text-[15px] leading-relaxed">{t("contact.subtitle")}</p>
          </div>

          {status === "ok" ? (
            <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-8 text-center" data-testid="contact-success">
              <CheckCircle2 size={32} className="text-[color:var(--gv-pink)] mx-auto mb-3" />
              <div className="font-display text-xl text-[color:var(--gv-ink)] mb-2">{t("contact.success.title")}</div>
              <p className="text-[color:var(--gv-ink)]/70 text-sm">{t("contact.success.desc")}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6 md:p-8 space-y-4" data-testid="contact-form" noValidate>
              {/* Honeypot — bots that blindly fill every input will
                  populate this hidden field; backend silently drops. */}
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0 }}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1.5">
                    {t("contact.field.name")}
                  </label>
                  <input
                    required value={form.name} onChange={update("name")}
                    className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--gv-pink)]"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1.5">
                    {t("contact.field.email")}
                  </label>
                  <input
                    required type="email" value={form.email} onChange={update("email")}
                    className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--gv-pink)]"
                    autoComplete="email"
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1.5">
                  {t("contact.field.subject")}
                </label>
                <input
                  required value={form.subject} onChange={update("subject")}
                  className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--gv-pink)]"
                  data-testid="contact-subject"
                />
              </div>
              <div>
                <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1.5">
                  {t("contact.field.message")}
                </label>
                <textarea
                  required value={form.message} onChange={update("message")} rows={6}
                  className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--gv-pink)] resize-y"
                  data-testid="contact-message"
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer pt-1">
                <input
                  type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 accent-[color:var(--gv-pink)]"
                  data-testid="contact-consent"
                />
                <span className="text-[12.5px] text-[color:var(--gv-ink)]/70 leading-snug">
                  {t("contact.consent.label")}
                </span>
              </label>
              {status === "err" && errorMsg && (
                <div className="flex items-start gap-2 text-[13px] text-[color:var(--gv-pink)]" data-testid="contact-error">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}
              <button
                type="submit" disabled={status === "sending"}
                className="btn-primary w-full justify-center disabled:opacity-50"
                data-testid="contact-submit"
              >
                {status === "sending" ? <Loader2 size={14} className="animate-spin" /> : (
                  <>{t("contact.submit")} <Mail size={14} /></>
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
